import Image from 'next/image';
import { useState } from 'react';
import Link from 'next/link';
import { X, ChevronDown, Phone, MapPin } from 'lucide-react';
import { services } from '../data/servicesMenuData';
import { useRouter } from 'next/router';

const Menu = (props) => {
  const { isOpen, setIsOpen, scrolled, linkColorClass } = props;
  const router = useRouter();  // Access the router

  // Function to determine if a link is active
const isActiveLink = (href, opts = {}) => {
  const path = router.asPath.split('?')[0];

  // SECTION MATCH (fixes /blog/page/2 issue)
  if (opts.startsWith) {
    return path.startsWith(href) ? 'text-primary' : '';
  }

  // EXACT MATCH
  return path === href ? 'text-primary' : '';
};

  const [dropdownOpen, setDropdownOpen] = useState(false);
          const isAreasActive = !!isActiveLink('/service-areas', { startsWith: true });

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden md:flex items-center ml-auto gap-6 text-sm uppercase tracking-[3px]">
        <Link href="/" className={`nav-link ${linkColorClass} ${isActiveLink('/')}`}>Home</Link>
        <Link href="/about-us" className={`nav-link ${linkColorClass} ${isActiveLink('/about-us')}`}>About</Link>

        {/* Services Dropdown */}
        <div className="group">
          <Link
            href="/services"
            className={`nav-link flex items-center gap-1 py-9 relative ${linkColorClass} ${isActiveLink('/services')}`}
          >
            Services <ChevronDown size={16} />
          </Link>

          <div className="absolute hidden group-hover:block left-0 right-0 bg-white shadow-md" style={{ top: '100%' }}>
            <div className={`container mx-auto grid grid-cols-3 gap-20 pr-10 pl-10 pb-20 ${scrolled ? 'pt-5' : 'pt-10'} max-w-6xl`}>
              {Object.entries(services).map(([title, links]) => (
                <div key={title}>
                  <h4 className="font-semibold uppercase text-primary mb-3 text-base">{title}</h4>
                  <ul className="space-y-2 text-black">
                    {links.map(link => (
                      <li key={link.href}>
                        <Link href={link.href} className={`hover:text-primary font-light flex gap-2 items-center ${isActiveLink(link.href)}`}>
                          <Image
                            src={link.image}
                            alt={`Modern Build ${link.name}`}
                            title={`Modern Build ${link.name}`}
                            width={1024}
                            height={1024}
                            className="w-7 h-7 object-cover"
                          />
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Link href="/projects" className={`nav-link ${linkColorClass} ${isActiveLink('/projects')}`}>Projects</Link>
        <Link href="/blog" className={`nav-link ${linkColorClass} ${isActiveLink('/blog', { startsWith: true })}`}>Blog</Link>
        <Link href="/financing" className={`nav-link ${linkColorClass} ${isActiveLink('/financing')}`}>Financing</Link>
        <Link href="/contact" className={`nav-link ${linkColorClass} ${isActiveLink('/contact')}`}>Contact</Link>

        <Link
          href="/service-areas"
          aria-label="Service Areas"
          aria-current={isAreasActive ? 'page' : undefined}
          className={`hidden lg:inline-flex items-center gap-1 nav-link ${linkColorClass} ${isActiveLink('/service-areas')}`}
        >
          <MapPin className="h-4 w-4" />
          <span>Areas</span>
        </Link>

        <Link href="tel:+13233004130" className="btn flex items-center gap-2">
          <span className="flex items-center gap-2">
            <Phone size={18} /> Call Us (323) 300-4130
          </span>
        </Link>
      </nav>

      {/* Mobile Menu */}
      {isOpen && (
        <div 
          className="fixed top-0 right-0 w-full h-screen bg-white transform transition-transform duration-1000 ease-in-out overflow-y-auto"
          style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}
        >
          <div className="flex justify-between px-4 pt-9 pb-4">
            <Link href="/">
              <div className="text-center">
                <p className="text-primary text-2xl tracking-[3px] font-gilda uppercase">Modern Build</p>
                <p className="text-black text-xs font-light tracking-[7px] uppercase">Home Remodeling</p>
              </div>
            </Link>
            <button aria-label="Close mobile menu" onClick={() => setIsOpen(false)} className="text-primary">
              <X size={24} />
            </button>
          </div>

          <div className="px-4 py-4 space-y-4">
            <Link href="/" className={`block mobile-menu-link text-xl ${isActiveLink('/')}`}>Home</Link>
            <Link href="/about-us" className={`block mobile-menu-link text-xl ${isActiveLink('/about-us')}`}>About</Link>
            {/* Mobile Services Dropdown */}
            <button
              aria-label="open services menu mobile"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`mobile-menu-link text-xl w-full flex justify-between ${isActiveLink('/services')}`}
            >
              Services <ChevronDown size={16} className={dropdownOpen ? 'rotate-180' : ''} />
            </button>
            {dropdownOpen && (
              <div className="pl-4 space-y-4">
                {Object.entries(services).map(([title, links]) => (
                  <div key={title}>
                    <h4 className="font-semibold uppercase text-primary mb-2 text-xl text-base">{title}</h4>
                    <ul className="space-y-2">
                      {links.map(link => (
                        <li key={link.href}>
                          <Link href={link.href} className={`hover:text-primary flex items-center font-light text-xl gap-2 ${isActiveLink(link.href)}`}>
                            <Image
                              src={link.image}
                              alt={link.name}
                              title={link.name}
                              width={1024}
                              height={1024}
                              className="w-7 h-7 object-cover"
                            />
                            {link.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {["Projects", "Blog", "Financing", "Contact"].map(text => (
              <Link key={text} href={`/${text.toLowerCase().replace(/ & /g, '-').replace(/\s/g, '-')}`} className={`block mobile-menu-link text-xl ${isActiveLink(`/${text.toLowerCase().replace(/ & /g, '-').replace(/\s/g, '-')}`)}`}>
                {text}
              </Link>
            ))}

            <Link href="/service-areas" className={`block mobile-menu-link text-xl ${isActiveLink('/service-areas')}`}>Service Areas</Link>

            <Link href="tel:+13233004130" className="btn block top-[30px] text-center items-center">
              <span className="inline-flex items-center gap-2 text-2xl">
                <Phone size={18} /> Call Us (323) 300-4130
              </span>
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default Menu;